import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import dayjs from 'dayjs';
import { LearnerSampleSetItem } from 'src/app/shared/models/learner-sample-set';
import { OperationResult, OperationResultWithValue } from 'src/app/shared/models/operation-result';
import { EquityReview, EquityReviewToCorrectiveActionPlanView } from '../../models/equity-review';
import { EquityReviewLearnerSearchCriteria } from '../../models/equity-review-learner-search-criteria';

@Injectable({
  providedIn: 'root',
})
export class EquityReviewService {
  private baseUrl = 'api/equity/reviews';

  constructor(private http: HttpClient) {}

  hasReviews(districtIds: string[]) {
    let params = new HttpParams();
    districtIds.forEach((districtId) => (params = params.append('districtIds', districtId)));
    return this.http.get<OperationResultWithValue<boolean>>(`${this.baseUrl}`, { params });
  }

  getReviewById(id: string) {
    return this.http.get<OperationResultWithValue<EquityReview>>(`${this.baseUrl}/${id}`);
  }

  getEquityCorrectiveActionPlan(id: string) {
    return this.http.get<OperationResultWithValue<EquityReviewToCorrectiveActionPlanView>>(
      `${this.baseUrl}/${id}/create-corrective-action-plan`
    );
  }

  createEquityCorrectiveActionPlan(id: string, model: any) {
    return this.http.post<OperationResultWithValue<string>>(`${this.baseUrl}/${id}/create-corrective-action-plan`, model);
  }

  getReviewByLocationId(id: string) {
    return this.http.get<OperationResultWithValue<EquityReview>>(`${this.baseUrl}/locations/${id}`);
  }

  updateDueDate(id: string, date: Date) {
    const dateStr = dayjs(date).startOf('day').toISOString();
    return this.http.put<OperationResult>(`${this.baseUrl}/${id}/due-date/${dateStr}`, null);
  }

  validateReviewStatus(id: string) {
    return this.http.get<OperationResultWithValue<any>>(`${this.baseUrl}/${id}/validate-status`);
  }

  openReview(id: string) {
    return this.http.post<OperationResultWithValue<EquityReview>>(`${this.baseUrl}/${id}/open-review`, null);
  }

  submitReview(id: string) {
    return this.http.post<OperationResultWithValue<EquityReview>>(`${this.baseUrl}/${id}/submit-review`, null);
  }

  finalizeReview(id: string) {
    return this.http.post<OperationResultWithValue<EquityReview>>(`${this.baseUrl}/${id}/finalize-review`, null);
  }

  search(model: EquityReviewLearnerSearchCriteria) {
    return this.http.post<OperationResultWithValue<LearnerSampleSetItem[]>>('api/equity/reviews/learner-search', model);
  }

  uploadAndParseFile(equityReviewId: string, categoryId: string, file: FormData) {
    return this.http.post<OperationResultWithValue<LearnerSampleSetItem[]>>(
      `${this.baseUrl}/${equityReviewId}/${categoryId}/parse-learners-state-id-file`,
      file,
      this.getMultipartRequestHeaders()
    );
  }

  private getMultipartRequestHeaders(): {
    headers: HttpHeaders | { [header: string]: string | string[] };
  } {
    const headers = new HttpHeaders({
      'Content-Disposition': 'multipart/form-data',
      Accept: 'application/json, text/plain, */*',
    });

    return { headers };
  }
}
